<script setup lang="ts">
import type { Subscription } from '@/services/subscription'
import type { ImageMap } from '@/services/productImage'
import { AsyncOperationState } from '@/stores/lib/AsyncOperationState'

import { ref } from 'vue'

import Badge from '@/components/Badge.vue'
import NextOrderUpsellItem from './NextOrderUpsellItem.vue'

defineProps<{
  subscriptions: Subscription[]
  nextOrderScheduledAt: string | null
  productImages: Record<string, ImageMap[] | null | undefined>
  workState: AsyncOperationState
  minimisable?: boolean
}>()

const selectedSubscriptionId = ref<number | null>(null)

const emit = defineEmits<{
  (e: 'add-to-next-order', subscriptionId: number): void
}>()

const onAddToNextOrder = (subscriptionId: number) => {
  selectedSubscriptionId.value = subscriptionId
  emit('add-to-next-order', subscriptionId)
}
</script>

<template>
  <component :is="minimisable ? 'details' : 'div'" class="AddToNextOrderUpsell">
    <component
      :is="minimisable ? 'summary' : 'div'"
      class="AddToNextOrderUpsell__Header"
    >
      Add your regular products
      <Badge v-if="minimisable" class="Badge--mini Badge--dark">{{
        subscriptions.length
      }}</Badge>
    </component>
    <div class="AddToNextOrderUpsell__Subscriptions">
      <NextOrderUpsellItem
        v-for="subscription in subscriptions"
        :key="subscription.id"
        :subscription="subscription"
        :images="
          productImages[
            `${subscription.external_product_id.ecommerce}:${subscription.external_variant_id.ecommerce}`
          ]
        "
        :working="
          workState === AsyncOperationState.IN_PROGRESS &&
          selectedSubscriptionId === subscription.id
        "
        @add-to-next-order="onAddToNextOrder"
      ></NextOrderUpsellItem>
    </div>
  </component>
</template>

<style>
.AddToNextOrderUpsell {
  background-color: var(--color-white);
  padding: 8px 0;
  border-radius: 8px;

  &div {
    background-color: var(--color-lichen-25);
    padding: 8px 0 0;

    & .AddToNextOrderUpsell__Header {
      margin: 8px 16px 0;
    }
  }

  & .AddToNextOrderUpsell__Header {
    margin: 0;
    display: flex;
    align-items: center;
    gap: 8px;
    line-height: 22px;
    font-weight: bold;

    & .Badge {
      font-weight: normal;
    }
  }

  & summary {
    position: relative;
    cursor: pointer;
    list-style: none;
    padding: 8px 24px 8px 16px;

    &::after {
      content: '';
      display: block;
      width: 16px;
      height: 16px;
      background-repeat: no-repeat;
      background-position: center;
      background-image: url('@/assets/icons/chevron.svg');
      position: absolute;
      right: 16px;
      top: 50%;
      margin-top: -8px;
      transform: rotate(270deg);
    }
  }

  &[open] summary::after {
    transform: rotate(90deg);
  }

  & summary::-webkit-details-marker {
    display: none;
  }

  & [data-content] {
    padding: 4px 12px 12px;
  }
}

.AddToNextOrderUpsell__Subscriptions {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px;
}

.AddToNextOrderUpsell__SubscriptionItem {
  display: flex;
  align-items: center;
  gap: 12px;

  & .ProductImage {
    flex-shrink: 0;
    border-radius: 4px;
    width: 44px;
    height: 44px;

    @media (--mq-tablet-and-up) {
      width: 88px;
      height: 88px;
    }
  }

  & strong {
    flex-grow: 1;
  }

  & .Button {
    flex-shrink: 0;
  }
}

.ProductSearch .AddToNextOrderUpsell {
  @media (--mq-tablet-and-up) {
    background-color: var(--color-lichen-25);
  }
}
</style>
